<template>
  <div
    class="task-card"
    :class="{
      'task-card_rating': type === 'rating',
      'task-card_special': isSpecial === true,
    }"
    @click.stop="onClick"
  >
    <div class="task-card__cover" :style="{'background-image': `url(${image || defaultCover})`}">
      <div v-if="isSpecial" class="task-card__label task-card__label_special">
        <Icon name="crown" size="xs" />
        <span v-html="styleLabels.special"></span>
      </div>
      <div v-else-if="isRepeat" class="task-card__label">
        <Icon name="sync" size="xs" />
        <span v-html="styleLabels.repeat"></span>
      </div>
      <div v-else-if="isDaily" class="task-card__label">
        <Icon name="calendar" size="xs" />
        <span v-html="styleLabels.daily"></span>
      </div>
      <div class="task-card__specifications">
        <div v-if="complexity" class="task-card__complexity" :class="getComplexityClass()">
          <img v-if="isLowComplexity" :src="complexityLow" alt="" />
          <img v-else-if="isMediumComplexity" :src="complexityMedium" alt="" />
          <img v-else-if="isHighComplexity" :src="complexityHigh" alt="" />
          <img v-else :src="complexityMaster" alt="" />
        </div>
        <div class="task-card__activities">
          <div class="task-card__activity" v-for="(item, index) in disciplines" :key="index">
            <Icon v-if="item.length > 0" :name="item" size="xs" />
          </div>
        </div>
      </div>
    </div>
    <div class="task-card__info">
      <div class="info__title" :class="state !== 'available' ? 'info__title_with-progress' : ''" v-html="title"></div>
      <div class="info__subtitle" v-html="subtitle" v-if="state === 'available'"></div>
      <div class="info__progress" :class="progressClass" v-else>
        <Badge
          v-if="badgeText"
          :state="state"
          :text="badgeText"
          class="progress__badge"
        />
        <Badge
          v-if="state === 'inProgress'"
          state="countdown"
          :text="countdownText"
          class="progress__badge"
        />
      </div>
      <div class="info__placeholder" :class="state !== 'available' ? 'info__placeholder_with-progress' : ''"></div>
      <div class="info__additional">
        <div class="info__value" :class="grayscaledClass">
          <div class="info__value-icon">
            <img :src="valueIcon"/>
          </div>
          <div class="info__value-points">
            {{ isFlexibleCost === true ? cost + '+' : cost }}
          </div>
        </div>
        <div class="info__state" :class="stateClass + (isSpecial ? ' info__state_special' : '')">
          <Icon v-if="stateIconName" :name="stateIconName" size="xs" />
          <span v-html="getStateLabel()"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import Icon from '@rr-component-library/icon/src/main';
import Badge from './Badge.vue';
import Coin from '../assets/img/coin.svg';
import Point from '../assets/img/point.svg';
import Low from '../assets/img/low.svg';
import Medium from '../assets/img/medium.svg';
import High from '../assets/img/high.svg';
import Master from '../assets/img/master.svg';
import Cover from '../assets/img/cover.png';

dayjs.extend(duration);
dayjs.extend(utc);

export default {
  name: 'TaskCard',
  designVersion: '1.2.0',
  components: {
    Icon,
    Badge,
  },
  props: {
    /**
     * Ссылка на верхнее изображение
     */
    image: {
      type: String,
    },
    /**
     * Действие по нажатию мышью на карточку<br>
     * `{ func: someFunction() { ... } }` вызвать функцию someFunction<br>
     * `{ link: '/some/link' }` перейти по ссылке '/some/link'
     */
    action: {
      type: Object,
    },
    /**
     * Тип карточки<br>
     * `'rating'` с очками рейтинга<br>
     * `'coins'` с баллами-монетами
     */
    type: {
      type: String,
      default: 'rating',
      validator(value) {
        return ['', 'rating', 'coins'].indexOf(value) !== -1;
      },
    },
    /**
     * Состояние задания<br>
     * `'available'` доступно<br>
     * `'inProgress'` выполняется<br>
     * `'checking'` проверяется<br>
     * `'completed'` завершено<br>
     * `'failed'` провалено<br>
     */
    state: {
      type: String,
      default: 'available',
      validator(value) {
        return ['', 'available', 'inProgress', 'checking', 'completed', 'failed'].indexOf(value) !== -1;
      },
    },
    /**
     * Названия для состояний задания<br>
     * `{ available: '', inProgress: '', checking: '',`<br>
     * `completed: '', failed: '' }`
     */
    stateLabels: {
      type: Object,
    },
    /**
     * Текст заголовка
     */
    title: {
      type: String,
    },
    /**
     * Текст подзаголовка
     */
    subtitle: {
      type: String,
    },
    /**
     * Цена задания (очки/монеты)
     */
    cost: {
      type: [String, Number],
    },
    /**
     * Дата для задания<br>
     * для `'available'` - крайний срок, когда можно взять задание<br>
     * для `'inProgress'` - крайний срок выполнения задания<br>
     * для `'checking'`, `'completed'` и `'failed'` - дата смены статуса на указанный
     */
    date: {
      type: String,
    },
    /**
     * Количество подзадач в этом задании
     */
    itemsTotal: {
      type: Number,
    },
    /**
     * Количество выполненных подзадач
     */
    itemsDone: {
      type: Number,
    },
    /**
     * Сложность задания
     */
    complexity: {
      type: Number,
    },
    /**
     * Надписи для стилей оформления задания<br>
     * `{ special: '', daily: '', repeat: '' }`
     */
    styleLabels: {
      type: Object,
    },
    /**
     * Надписи для обратного отсчета<br>
     * `{ days: 'д', hours: 'ч' },`
     */
    timeLabels: {
      type: Object,
    },
    /**
     * Гибкая ли цена у задания (тогда `cost` - это минимальная цена)
     */
    isFlexibleCost: {
      type: Boolean,
    },
    /**
     * Стиль "особое" в оформлении задания<br>
     * Перекрывает остальные стили.
     */
    isSpecial: {
      type: Boolean,
    },
    /**
     * Стиль "ежедневное" в оформлении задания
     */
    isDaily: {
      type: Boolean,
    },
    /**
     * Стиль "многоразовое" в оформлении задания
     */
    isRepeat: {
      type: Boolean,
    },
    /**
     * Дисциплины
     */
    disciplines: {
      type: [],
    }
  },
  data() {
    return {
      defaultCover: Cover,
      coinImage: Coin,
      pointImage: Point,
      complexityLow: Low,
      complexityMedium: Medium,
      complexityHigh: High,
      complexityMaster: Master,
      dateText: '',
      dateValue: null,
      countdownText: '',
      isLongCountDown: false,
    };
  },
  computed: {
    stateIconName() {
      const iconNames = {
        inProgress: 'pulse',
        checking: 'hourglass',
        completed: 'check-bold',
        failed: 'close',
      }
      return iconNames[this.state] || '';
    },
    stateClass() {
      return `info__state_${this.state.toLowerCase()}`;
    },
    progressClass() {
      return `info__progress_${this.state.toLowerCase()}`;
    },
    valueIcon() {
      if (this.type === 'rating') return this.pointImage;
      else if (this.type === 'coins') return this.coinImage;
      else return '';
    },
    grayscaledClass() {
      const grayscaledStates = ['checking', 'failed'];
      if (grayscaledStates.includes(this.state)) return 'grayscaled';
      return '';
    },
    badgeText() {
      if (this.state === 'inProgress') {
        if (this.itemsTotal !== undefined && this.itemsDone !== undefined) {
          return `${this.itemsDone}/${this.itemsTotal}`;
        }
        return '';
      } else {
        return this.stateLabels[this.state] || '';
      }
    },
    isLowComplexity() {
      return this.complexity <= 1.3;
    },
    isMediumComplexity() {
      return this.complexity > 1.3 && this.complexity <= 1.8;
    },
    isHighComplexity() {
      return this.complexity > 1.8 && this.complexity <= 2.4;
    },
    isMasterComplexity() {
      return this.complexity > 2.4;
    },
  },
  methods: {
    onClick() {
      if (!this.action) return;
      if (this.action.link) {
        location.href = this.action.link;
        return;
      }
      if (this.action.func && typeof this.action.func === 'function') {
        this.action.func();
      }
    },
    getStateLabel() {
      let label = '';
      if (this.state === 'available') {
        if (this.isSpecial && !this.isLongCountDown) {
          label = this.countdownText;
        } else {
          if (this.stateLabels) {
            label = this.stateLabels[this.state] || '';
          }
          label += this.dateText;
        }
      } else if (this.state === 'inProgress') {
        if (this.stateLabels) {
          label = this.stateLabels[this.state] || '';
        }
      } else {
        label = this.dateText;
      }
      return label;
    },
    getComplexityClass() {
      if (this.isLowComplexity) {
        return 'task-card__complexity_low';
      } else if (this.isMediumComplexity) {
        return 'task-card__complexity_medium';
      } else if (this.isHighComplexity) {
        return 'task-card__complexity_high';
      } else {
        return 'task-card__complexity_master';
      }
    },
    setCountdown() {
      const value = dayjs.utc(this.date);
      if (value.isValid()) {
        this.dateValue = value;
        this.dateText = value.format('DD.MM.YYYY');
        this.updateCountdown();
      }
    },
    updateCountdown() {
      const seconds = this.dateValue.diff(dayjs.utc(), 'seconds');
      if (seconds <= 0) {
        this.countdownText = '00:00:00';
        return;
      }
      this.countdownText = this.formatCountdown(seconds);
      setTimeout(() => { this.updateCountdown(); }, this.isLongCountDown ? 60000 : 1000);
    },
    formatCountdown(seconds) {
      const duration = dayjs.duration(seconds * 1000);
      const days = duration.asDays();
      if (days >= 1) {
        let d = 0;
        let h = 0;
        if (Number.parseInt(days, 10) !== days) {
          d = Number.parseInt(days, 10);
          h = Math.round(duration.add(-d, 'day').asHours());
        } else {
          d = days;
        }
        this.isLongCountDown = true;
        return `${d}${this.timeLabels.days || 'д'} ${h}${this.timeLabels.hours || 'ч'}`;
      }
      this.isLongCountDown = false;
      return duration.format('HH:mm:ss');
    },
  },
  mounted() {
    this.setCountdown();
  },
};
</script>

<style lang="scss" scoped>
  @import "./TaskCard";
</style>
